/**
 * GA4 Custom Events for Kashkha
 * Author: Maik Gossen / DD-Gossen
 *
 * All delegated click listeners use the CAPTURE phase because Halo's
 * theme JS stops propagation on tabs and similar UI elements.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  function push(event, params) {
    var payload = Object.assign({ event: event }, params || {});
    window.dataLayer.push(payload);
    if (window.KASHKHA_GA4_DEBUG) {
      console.log('[GA4] pushed', payload);
    }
  }

  window.kashkhaGa4 = { push: push };

  // ================================================================
  // customer_signup
  // ================================================================
  function trackCustomerSignup() {
    var registerForm = document.querySelector('form[action*="/account"][action*="create"]');
    if (registerForm) {
      registerForm.addEventListener('submit', function () {
        push('customer_signup', { method: 'form' });
      });
    }
  }

  // ================================================================
  // view_cart_drawer
  // ================================================================
  function trackCartDrawer() {
    var selectors = [
      '#halo-cart-sidebar', '.halo-cart-sidebar', '.previewCart-wrapper',
      '.previewCartWrapper', '.mini-cart', 'cart-drawer', '#cart-drawer',
      '.cart-drawer', '#sidebar-cart',
    ];
    var drawer = null;
    for (var i = 0; i < selectors.length; i++) {
      drawer = document.querySelector(selectors[i]);
      if (drawer) break;
    }
    if (!drawer) return;

    var wasVisible = isElementVisible(drawer);
    var lastFiredAt = 0;
    function fireIfNotDuplicate() {
      var now = Date.now();
      if (now - lastFiredAt < 500) return;
      lastFiredAt = now;
      push('view_cart_drawer', {});
    }

    var observer = new MutationObserver(function () {
      var visible = isElementVisible(drawer);
      if (visible && !wasVisible) fireIfNotDuplicate();
      wasVisible = visible;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });

    var bodyObserver = new MutationObserver(function () {
      setTimeout(function () {
        var visible = isElementVisible(drawer);
        if (visible && !wasVisible) fireIfNotDuplicate();
        wasVisible = visible;
      }, 150);
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  function isElementVisible(el) {
    if (!el) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // ================================================================
  // coupon_code_click
  //
  // Two locations on Kashkha's storefront:
  //   A) Cart drawer (any page): <coupon-code> element with an
  //      "Add A Coupon" label, input, and a Save button.
  //   B) Cart page /cart: an input inside .cart-coupon-code
  //      (no save button, code is applied on checkout).
  //
  // Sources emitted:
  //   "open"      → user clicked the trigger / label to reveal the input
  //   "save"      → user clicked Save in the drawer (input value captured)
  //   "cart_page" → user typed a code on /cart and moved on (blur/change)
  // ================================================================
  function trackCouponClick() {
    var debounceUntil = 0;

    // ---- Click delegation (open / save) ----
    document.addEventListener('click', function (e) {
      var now = Date.now();
      if (now < debounceUntil) return;

      // Save button (cart drawer only)
      var saveBtn = e.target.closest('[data-update-coupon], coupon-code button');
      if (saveBtn) {
        var inputEl = document.querySelector('#coupon-code, coupon-code input[name="discount"]');
        var codeVal = inputEl ? (inputEl.value || '').trim() : '';
        debounceUntil = now + 500;
        push('coupon_code_click', {
          source: 'save',
          coupon_text: codeVal.slice(0, 40)
        });
        return;
      }

      // Opener / trigger (drawer trigger, cart-page container, coupon field)
      var opener = e.target.closest(
        'coupon-code, .discount-code-field, .cart-tool-discount, .cartTool-item.cart-tool-discount, .cart-coupon-code, .cart-total-coupon, [data-coupon], .cart-coupon, .apply-coupon, .discount-code-btn, .promo-code, .coupon-code, .halo-coupon'
      );
      if (opener) {
        if (opener.tagName === 'LINK' || opener.tagName === 'SCRIPT') return;
        var href = opener.getAttribute && opener.getAttribute('href') || '';
        if (href.indexOf('/cdn/') !== -1 || href.indexOf('.js') !== -1 || href.indexOf('.css') !== -1) return;

        debounceUntil = now + 500;
        push('coupon_code_click', {
          source: 'open',
          coupon_text: (opener.textContent || '').trim().slice(0, 60)
        });
      }
    }, true);

    // ---- Change / blur on /cart discount input (no save button there) ----
    // We fire once per unique value so we don't double-track when the user
    // clicks in and out of the field without changing the code.
    var lastTracked = '';
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.id !== 'cart-coupon-code' && t.name !== 'discount') return;
      // Don't double-track the drawer input, that one is handled by the Save click
      if (t.id === 'coupon-code') return;
      var v = (t.value || '').trim();
      if (!v || v === lastTracked) return;
      lastTracked = v;
      push('coupon_code_click', {
        source: 'cart_page',
        coupon_text: v.slice(0, 40)
      });
    }, true);
  }

  // ================================================================
  // variant_click
  // ================================================================
  function trackVariantClick() {
    document.addEventListener('change', function (e) {
      if (!document.body.classList.contains('template-product')) return;
      var target = e.target;
      if (!target || !target.tagName) return;

      if (target.tagName === 'INPUT' && target.type === 'radio') {
        var name = target.name || '';
        var lower = name.toLowerCase();
        if (lower !== 'color' && lower !== 'size') return;
        var inProductForm = target.closest('.product-form__input, .product-form__swatch, form[action*="/cart/add"]');
        if (!inProductForm) return;
        push('variant_click', {
          variant_type: name,
          variant_label: target.value || ''
        });
        return;
      }

      if (target.tagName === 'SELECT' && (target.name || '').indexOf('stick-variant') === 0) {
        var selectedOption = target.options[target.selectedIndex];
        push('variant_click', {
          variant_type: 'sticky_variant',
          variant_label: selectedOption ? selectedOption.text : (target.value || '')
        });
      }
    }, true);
  }

  // ================================================================
  // take_closer_look
  // ================================================================
  function trackCloserLook() {
    if (!document.body.classList.contains('template-product')) return;
    var section = findCloserLookSection();
    if (!section) return;

    section.addEventListener('click', function () {
      push('take_closer_look_click', {});
    }, true);

    var dwellFired = false;
    var visibleSince = null;
    var checkInterval = null;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!visibleSince) visibleSince = Date.now();
          if (!checkInterval && !dwellFired) {
            checkInterval = setInterval(function () {
              if (visibleSince && Date.now() - visibleSince >= 2000 && !dwellFired) {
                dwellFired = true;
                push('take_closer_look_view', { dwell_ms: 2000 });
                clearInterval(checkInterval);
                checkInterval = null;
              }
            }, 250);
          }
        } else {
          visibleSince = null;
          if (checkInterval) { clearInterval(checkInterval); checkInterval = null; }
        }
      });
    }, { threshold: [0, 0.5, 1] });

    io.observe(section);
  }

  function findCloserLookSection() {
    var byId = document.querySelector('#take-a-closer-look, .take-a-closer-look, [data-section="take-a-closer-look"], .product-features__heading');
    if (byId && byId.className.indexOf('heading') !== -1) {
      return byId.closest('section, .halo-section, .shopify-section, div');
    }
    if (byId) return byId;
    var headings = document.querySelectorAll('h2, h3, .section-title, .halo-heading, .title-section');
    for (var i = 0; i < headings.length; i++) {
      var text = (headings[i].textContent || '').trim().toLowerCase();
      if (text.indexOf('take a closer look') !== -1 || text.indexOf('closer look') !== -1) {
        return headings[i].closest('section, .halo-section, .shopify-section, div');
      }
    }
    return null;
  }

  // ================================================================
  // size_guide_open
  // ================================================================
  function trackSizeGuide() {
    var debounceUntil = 0;
    document.addEventListener('click', function (e) {
      var now = Date.now();
      if (now < debounceUntil) return;

      var tabTrigger = e.target.closest('.tab-title, .toggleLink, .toggle-title, [data-tab]');
      if (tabTrigger) {
        var tabText = (tabTrigger.textContent || '').trim().toLowerCase();
        var tabHref = (tabTrigger.getAttribute('href') || '').toLowerCase();
        if (tabText.indexOf('size chart') !== -1 || tabText.indexOf('size guide') !== -1 ||
            tabHref.indexOf('size chart') !== -1 || tabHref.indexOf('size-chart') !== -1 ||
            tabHref.indexOf('size guide') !== -1 || tabHref.indexOf('size-guide') !== -1) {
          debounceUntil = now + 500;
          push('size_guide_open', { source: 'tab' });
          return;
        }
      }

      var btn = e.target.closest(
        '[data-size-guide], .size-guide-btn, .size-guide-link, .halo-size-chart, .size-chart-btn'
      );
      if (btn) {
        debounceUntil = now + 500;
        push('size_guide_open', { source: 'button' });
        return;
      }

      var link = e.target.closest('a[href]');
      if (link) {
        var href = (link.getAttribute('href') || '').toLowerCase();
        if (href.indexOf('/size-guide') !== -1 || href.indexOf('/size_guide') !== -1 ||
            href.indexOf('/sizeguide') !== -1 || href.indexOf('/size-guides') !== -1 ||
            href.indexOf('140110726') !== -1) {
          debounceUntil = now + 500;
          push('size_guide_open', { source: 'link' });
          return;
        }
      }
    }, true);
  }

  // ================================================================
  // Init
  // ================================================================
  function init() {
    try {
      trackCustomerSignup();
      trackCartDrawer();
      trackCouponClick();
      trackVariantClick();
      trackCloserLook();
      trackSizeGuide();
    } catch (err) {
      console.warn('[GA4 custom events] init error:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
